import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./ShortTerm.css";

const ShortTermMemoryTest = () => {
  const navigate = useNavigate();

  const [service, setService] = useState("");
  const [hasAccess, setHasAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  // ==========================================
  // SCROLL TO TOP
  // ==========================================

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  // ==========================================
  // ACCESS CHECK + SERVICE
  // ==========================================

  useEffect(() => {
    let isMounted = true;

    const checkAccess = async () => {
      const email = localStorage.getItem("user_email");
      const authed = localStorage.getItem("rg_auth") === "1";

      if (!authed || !email) {
        navigate("/dashboard-login");
        return;
      }

      try {
        const res = await axios.get("/check-access", {
          params: {
            email,
            test: "short_term_memory",
          },
        });

        console.log("Short-Term Memory API:", res.data);

        if (!res.data.allowed) {
          if (isMounted) {
            setHasAccess(false);
          }
          return;
        }

        // Get service from API response or localStorage
        const userService =
          res.data.service ||
          res.data.metadata?.service ||
          res.data.user?.metadata?.service ||
          localStorage.getItem("service") ||
          "";

        if (isMounted) {
          setService(userService);
          setHasAccess(true);
        }
      } catch (err) {
        console.error(
          "Short-term memory access check failed:",
          err
        );

        if (isMounted) {
          setHasAccess(false);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    checkAccess();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  // ==========================================
  // SERVICE IDENTIFICATION
  // ==========================================

  const normalizedService = service.trim().toLowerCase();

  const isVisionIgnite =
    normalizedService === "forge your peak - vision ignite";

  const isIgniteOrAscend =
    normalizedService === "forge your peak - ignite" ||
    normalizedService === "forge your peak - ascend";

  // ==========================================
  // RETURN HOME
  // ==========================================

  const returnHome = () => {
    if (isVisionIgnite) {
      navigate("/measure");
    } else if (isIgniteOrAscend) {
      navigate("/ignite/dashboard");
    } else {
      navigate("/");
    }
  };

  // ==========================================
  // START TEST
  // ==========================================

  const startTest = () => {
    navigate("/short-term-memory/play");
  };

  // ==========================================
  // LOADING
  // ==========================================

  if (loading || hasAccess === null) {
    return (
      <div className="rg-login-wrapper">
        <p style={{ textAlign: "center" }}>
          Checking access...
        </p>
      </div>
    );
  }

  // ==========================================
  // ACCESS DENIED
  // ==========================================

  if (hasAccess === false) {
    return (
      <div className="access-denied-container">
        <h2>🚫 Access Denied</h2>

        <p>
          You don't have access to this test.
        </p>

        <button
          type="button"
          onClick={() => navigate("/")}
        >
          Go Back
        </button>
      </div>
    );
  }

  // ==========================================
  // SERVICE-BASED CONTENT
  // Ignite + Ascend = Original content
  // Vision Ignite = Separate content
  // ==========================================

  const pageTitle = isVisionIgnite
    ? "Short-Term Memory Assessment"
    : "Short-Term Memory Test";

  const pageDescription = isVisionIgnite
    ? "Short-term memory is your ability to briefly store and recall information. This assessment helps you explore your memory, attention, and information-recall performance."
    : "Short-term memory is your ability to store small amounts of information for a brief period and recall it quickly. It trains your brain to See → Store → Recall.";

  return (
    <div className="rg-login-wrapper">

      {/* ==========================================
          RETURN HOME BUTTON
      ========================================== */}

      <div className="return-home-wrapper">
        <button
          type="button"
          className="return-home-btn"
          onClick={returnHome}
        >
          ← Return Home
        </button>
      </div>

      {/* ==========================================
          HEADER
      ========================================== */}

      <div className="reaction-header-card">

        <h2 className="reaction-title">
          {pageTitle}
        </h2>

        <p className="reaction-subtitle">
          {pageDescription}
        </p>

        <div className="reaction-pill">
          Test type:{" "}
          <strong>
            Memory → Short-Term Memory
          </strong>
        </div>

        <hr className="reaction-divider" />

        {/* ==========================================
            WHY SECTION
        ========================================== */}

        <div className="reaction-why-card">

          <h3 className="why-title">
            {isVisionIgnite
              ? "Why Short-Term Memory Matters"
              : "Why short-term memory matters"}
          </h3>

          {isVisionIgnite ? (
            <>
              {/* VISION IGNITE CONTENT */}

              <p className="why-description">
                Short-term memory helps you temporarily retain
                information while processing what you see.
                It supports visual attention, recall, and
                information processing.
              </p>

              <div className="why-tags">
                <span>Visual Memory</span>
                <span>Attention</span>
                <span>Quick Recall</span>
                <span>Information Processing</span>
                <span>Concentration</span>
              </div>

              <ul className="why-list">
                <li>
                  <strong>Visual memory:</strong>{" "}
                  Observe and remember information presented
                  on the screen.
                </li>

                <li>
                  <strong>Attention:</strong>{" "}
                  Focus on the information before it disappears.
                </li>

                <li>
                  <strong>Recall:</strong>{" "}
                  Retrieve the information you briefly observed.
                </li>

                <li>
                  <strong>Accuracy:</strong>{" "}
                  Monitor how accurately you reproduce the
                  remembered information.
                </li>
              </ul>
            </>
          ) : (
            <>
              {/* ORIGINAL IGNITE + ASCEND CONTENT */}

              <p className="why-description">
                Short-term memory is essential for quick thinking
                and real-time actions. It helps you process
                information instantly — useful in exams,
                conversations, and fast decision-making situations.
              </p>

              <div className="why-tags">
                <span>Quick recall ability</span>
                <span>Better concentration</span>
                <span>Improved learning speed</span>
                <span>Stronger mental clarity</span>
                <span>Faster reactions</span>
              </div>
            </>
          )}

        </div>
      </div>

      {/* ==========================================
          HOW THE GAME WORKS
      ========================================== */}

      <div className="how-card">

        <h2 className="how-title">
          How the game works
        </h2>

        <ul className="how-list">

          <li>
            A set of numbers or patterns will appear briefly.
          </li>

          <li>
            Observe and remember them carefully.
          </li>

          <li>
            After they disappear, reproduce what you saw.
          </li>

          <li>
            Difficulty increases as you progress.
          </li>

          <li>
            The game ends when you make an error.
          </li>

        </ul>

        <p className="how-note">
          Tip: Focus sharply — even 1 second matters.
        </p>

      </div>

      {/* ==========================================
          START TEST
      ========================================== */}

      <div className="start-test-section">

        <h2>
          {isVisionIgnite
            ? "Ready to Assess Your Short-Term Memory?"
            : "Ready to test your memory?"}
        </h2>

        <button
          type="button"
          className="start-test-btn"
          onClick={startTest}
        >
          Start Test
        </button>

      </div>

    </div>
  );
};

export default ShortTermMemoryTest;